import React, { useContext, useEffect, useRef, useState } from "react";
import { FilterContext } from "../../context/Filter";
import { fetchcustdetail } from "./Newpvinkpi";
import BasicModal from "../../components/Modals/BasicModal";
import PurchasedQuantityPvin from "./PurchasedQuantityPvin";
import SixMonthsQuantityPvin from "./SixMonthsQuantityPvin";

const FleetSummary = ({ data = [] }) => {
  const { childArn, superArn: ParArn } = useContext(FilterContext);

  // state
  const [custdet, setCustdet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetched, setFetched] = useState(false);
  const [error, setError] = useState(null);
  const [purchasedModal, setPurchasedModal] = useState(false);
  const [sixMonthPurchaseModal, setSixMonthPurchaseModal] = useState(false);

  // derive ARN key/value dynamically
  const getDynamicArn = () => {
    if (ParArn) return { key: "parArn", value: ParArn };
    if (childArn) return { key: "childArn", value: childArn };

    // fallback to vin from URL
    const urlParams = new URLSearchParams(window.location.search);
    const vin = urlParams.get("vin");
    if (vin) return { key: "parArn", value: vin };

    return { key: null, value: null };
  };

  const { key, value } = getDynamicArn();
  const payload = value ? { [key]: value } : null;

  const activeRequest = useRef(0);

  useEffect(() => {
    if (!value) {
      setCustdet(null);
      setFetched(false);
      setError(null);
      setLoading(false);
      return;
    }

    const reqId = ++activeRequest.current;
    setLoading(true);
    setFetched(false);
    setError(null);

    const getCustomerDetail = async () => {
      try {
        const res = await fetchcustdetail(payload);
        if (reqId !== activeRequest.current) return;

        const result = res?.data?.result || res?.result || res || null;
        setCustdet(result);
        setError(null);
      } catch (err) {
        if (reqId !== activeRequest.current) return;
        console.error("❌ Error fetching fleet summary:", err);
        setError("Failed to fetch fleet summary.");
        setCustdet(null);
      } finally {
        if (reqId !== activeRequest.current) return;
        setLoading(false);
        setFetched(true);
      }
    };

    getCustomerDetail();

    return () => {
      activeRequest.current++;
    };
  }, [value]);

  const {
    FleetSize = "—",
    last_six_month_value = "—",
    Purchase_Qty = "—",
  } = custdet || {};

  if (!value) {
    return (
      <CardShell title="Fleet Summary">
        <div className="flex justify-center items-center h-full text-gray-500 text-sm">
          No customer selected.
        </div>
      </CardShell>
    );
  }

  if (loading) {
    return (
      <CardShell title="Fleet Summary">
        <div className="flex justify-center items-center h-full">
          <div className="flex flex-col items-center text-gray-500">
            <div className="w-8 h-8 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
            <p className="text-sm mt-2">Loading fleet summary...</p>
          </div>
        </div>
      </CardShell>
    );
  }

  if (fetched && error) {
    return (
      <CardShell title="Fleet Summary">
        <div className="flex justify-center items-center h-full text-red-500 text-sm">
          {error}
        </div>
      </CardShell>
    );
  }

  if (fetched && !custdet) {
    return (
      <CardShell title="Fleet Summary">
        <div className="flex justify-center items-center h-full text-gray-500 text-sm">
          No fleet summary data found.
        </div>
      </CardShell>
    );
  }

  const firstItem = custdet?.lastPurchaseDateModalAndFleetSize?.[0];
  const firstHit = firstItem?.account_info?.hits?.hits?.[0];

  const fleetSize = firstItem?.Fleet_size ?? "N/A";
  const lastPurchasedDate = firstHit?._source?.FIRST_SALE_DT_DATE ?? "N/A";
  const lastPurchasModel = firstHit?._source?.PL ?? "N/A";

  return (
    <CardShell title="Fleet Summary">
      <div className="flex flex-col gap-2 text-sm mt-2">
        <Row label="TML Fleet Size">{fleetSize?.doc_count ?? "—"}</Row>
        <Row label="Last Purchase Date" even>
          {lastPurchasedDate?.split("T")?.[0] ?? "—"}
        </Row>
        <Row label="Last Purchase Model">{lastPurchasModel ?? "—"}</Row>

        <Row label="Purchase Quantity" even>
          <BasicModal
            open={purchasedModal}
            handleOpenModal={() => setPurchasedModal(true)}
            handleCloseModal={() => setPurchasedModal(false)}
            title="Purchase Quantity"
            trigger={
              <p
                className="font-medium text-blue-600 cursor-pointer"
                onClick={() => setPurchasedModal(true)}
              >
                {data?.Purchase_Qty ?? Purchase_Qty ?? "—"}
              </p>
            }
          >
            <PurchasedQuantityPvin />
          </BasicModal>
        </Row>

        <Row label="Last 6 Months Purchase Qty">
          <BasicModal
            open={sixMonthPurchaseModal}
            handleOpenModal={() => setSixMonthPurchaseModal(true)}
            handleCloseModal={() => setSixMonthPurchaseModal(false)}
            title="6 Month Purchase Quantity"
            trigger={
              <p
                className="font-medium text-blue-600 cursor-pointer"
                onClick={() => setSixMonthPurchaseModal(true)}
              >
                {data?.Last_6_Month_Purchase_Qty ?? last_six_month_value ?? "—"}
              </p>
            }
          >
            <SixMonthsQuantityPvin />
          </BasicModal>
        </Row>
      </div>
    </CardShell>
  );
};

export default FleetSummary;

const CardShell = ({ title, children }) => (
  <div className="w-[331px] h-[349px] border rounded-md flex flex-col rounded-t-md shadow-sm bg-white">
    <p className="rounded-t-md bg-[#056db3] p-1 text-white">{title}</p>
    <div className="border-b mb-2"></div>
    <div className="flex-1">{children}</div>
  </div>
);

const Row = ({ label, children, even }) => (
  <div
    className={`flex items-center justify-between rounded-sm p-2 ${even ? "bg-gray-100" : ""
      }`}
  >
    <p>{label}:</p>
    <p className="font-medium text-right">{children}</p>
  </div>
);
